// ============================================================================
// THE CYCLE — Settings Page
// Comprehensive app preferences, display controls, and system health.
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Switch, FormControlLabel, Chip,
  Divider, Alert, CircularProgress, Stack, List, ListItem, ListItemIcon,
  ListItemText, alpha, useTheme, ToggleButtonGroup, ToggleButton,
  Slider, Button, Select, MenuItem, FormControl, Tooltip, TextField, InputAdornment,
  Tabs, Tab, IconButton,
} from '@mui/material';
import {
  Settings as SettingsIcon, Storage, Wifi, Speed, CalendarMonth,
  CheckCircle, Error as ErrorIcon, Refresh, DataObject, ViewCompact,
  ViewComfy, ViewStream, Animation, AutorenewRounded, TableRows,
  Palette, BarChart, Thermostat, RestartAlt, SportsCricket, SportsBaseball,
  WorkspacePremium, OpenInNew, NotificationsActive, CardGiftcard, TrendingUp, Lock,
  Email, AccessTime, PersonAdd, Groups, Whatshot, AutoAwesome,
  Close, Add, Save, MonetizationOn,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useYear } from '../context/YearContext';
import { useSettings } from '../context/SettingsContext';
import { healthCheck } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

// ── MLB teams list ────────────────────────────────────────────────────────────
const MLB_TEAMS = [
  'ARI','ATL','BAL','BOS','CHC','CHW','CIN','CLE','COL','DET',
  'HOU','KC','LAA','LAD','MIA','MIL','MIN','NYM','NYY','OAK',
  'PHI','PIT','SD','SF','SEA','STL','TB','TEX','TOR','WSH',
];

const DIGEST_MODULES = [
  { key: 'cvrMovers',       label: 'CVR Movers',        color: '#2e7d32', description: 'Top 5 CVR risers & fallers daily — the market inefficiencies no other site surfaces.' },
  { key: 'hotColdStreaks',  label: 'Hot / Cold Streaks', color: '#e65100', description: 'Players on notable multi-game hot or cold streaks as of yesterday.' },
  { key: 'playerWatchlist', label: 'Player Watchlist',   color: '#1565c0', description: 'Daily CVR snapshot for specific players you follow.' },
  { key: 'teamPulse',       label: 'Team Pulse',         color: '#6a1b9a', description: 'Daily roster CVR summary for your followed teams.' },
  { key: 'leagueInsights',  label: 'League Insights',    color: '#0277bd', description: 'AI-generated daily summary of league trends, standouts, and emerging storylines.' },
  { key: 'fantasyEdge',     label: 'Fantasy Edge',       color: '#c62828', description: "CVR-driven DFS plays — high-value pickups and bust alerts for today's slate." },
];

const DEFAULT_EMAIL_PREFS = {
  enabled: false,
  deliveryTime: '8am',
  modules: { cvrMovers: true, hotColdStreaks: false, playerWatchlist: false, teamPulse: false, leagueInsights: false, fantasyEdge: false },
  watchedPlayers: [],
  followedTeams: [],
};

// ── Email Digests Tab ────────────────────────────────────────────────────────
function EmailDigestsTab({ isPaid, startCheckout, user }) {
  const theme = useTheme();
  const [prefs, setPrefs] = React.useState(DEFAULT_EMAIL_PREFS);
  const [dirty, setDirty] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [savedAt, setSavedAt] = React.useState(null);
  const [saveError, setSaveError] = React.useState(null);
  const [checkoutLoading, setCheckoutLoading] = React.useState(false);
  const [playerName, setPlayerName] = React.useState('');
  const [playerTeam, setPlayerTeam] = React.useState('');
  const [playerThreshold, setPlayerThreshold] = React.useState('');

  React.useEffect(() => {
    if (!isPaid) { setLoading(false); return; }
    const token = localStorage.getItem('tc-auth-token');
    if (!token) { setLoading(false); return; }
    fetch('/api/alerts/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (d.emailPrefs) {
          setPrefs({
            ...DEFAULT_EMAIL_PREFS, ...d.emailPrefs,
            modules: { ...DEFAULT_EMAIL_PREFS.modules, ...d.emailPrefs.modules },
          });
          if (d.emailPrefs.updatedAt) {
            setSavedAt(new Date(d.emailPrefs.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isPaid]);

  const update = (path, value) => {
    setPrefs(p => {
      if (path === 'enabled')      return { ...p, enabled: value };
      if (path === 'deliveryTime') return { ...p, deliveryTime: value };
      if (path.startsWith('modules.')) {
        const k = path.slice(8);
        return { ...p, modules: { ...p.modules, [k]: value } };
      }
      return { ...p, [path]: value };
    });
    setDirty(true);
  };

  const save = async () => {
    const token = localStorage.getItem('tc-auth-token');
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch('/api/alerts/preferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(prefs),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setDirty(false);
      setSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const addPlayer = () => {
    const name = playerName.trim();
    if (!name || prefs.watchedPlayers.length >= 20) return;
    update('watchedPlayers', [...prefs.watchedPlayers, {
      name,
      team: playerTeam.trim().toUpperCase(),
      cvrThreshold: playerThreshold ? parseInt(playerThreshold, 10) : null,
    }]);
    setPlayerName(''); setPlayerTeam(''); setPlayerThreshold('');
  };

  const removePlayer = (idx) =>
    update('watchedPlayers', prefs.watchedPlayers.filter((_, i) => i !== idx));

  const toggleTeam = (abbr) => {
    const next = prefs.followedTeams.includes(abbr)
      ? prefs.followedTeams.filter(t => t !== abbr)
      : [...prefs.followedTeams, abbr];
    update('followedTeams', next);
  };

  if (!isPaid) {
    return (
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, textAlign: 'center' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Email sx={{ fontSize: 44, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>Pro Feature</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            Daily AI-personalized email digests with CVR movers, player watchlist alerts, team pulse summaries, hot/cold streaks, and more.
          </Typography>
          <Button
            variant="contained" size="medium"
            startIcon={<WorkspacePremium />}
            disabled={checkoutLoading}
            onClick={async () => {
              setCheckoutLoading(true);
              try { await startCheckout(); } finally { setCheckoutLoading(false); }
            }}
            sx={{
              textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #1a3a6e, #e8722a)',
              '&:hover': { background: 'linear-gradient(135deg, #162f5a, #c96520)' },
            }}
          >
            {checkoutLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Upgrade — $8/mo'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  }

  const accentBg = (color) => alpha(color, theme.palette.mode === 'dark' ? 0.18 : 0.07);

  return (
    <Box>
      {/* Master on/off */}
      <Card elevation={0} sx={{ mb: 2.5, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.15)}` }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, pb: '20px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.palette.primary.main }}>
                <Email />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>Daily Email Digest</Typography>
                <Typography variant="caption" color="text.secondary">Personalized morning briefing → {user?.email}</Typography>
              </Box>
            </Box>
            <Switch checked={prefs.enabled} onChange={e => update('enabled', e.target.checked)} sx={{ flexShrink: 0 }} />
          </Box>
        </CardContent>
      </Card>

      {prefs.enabled && (
        <>
          {/* Delivery time */}
          <Card elevation={0} sx={{ mb: 2.5, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, pb: '20px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <AccessTime sx={{ fontSize: 17, color: 'text.secondary' }} />
                <Typography variant="subtitle2" fontWeight={700}>Delivery Time</Typography>
                <Chip label="US Eastern" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} />
              </Box>
              <ToggleButtonGroup
                value={prefs.deliveryTime}
                exclusive
                onChange={(_, v) => v && update('deliveryTime', v)}
                size="small"
                sx={{ '& .MuiToggleButton-root': { px: 2.5, fontWeight: 600, textTransform: 'none' } }}
              >
                {['6am', '7am', '8am', '9am', '10am'].map(t => (
                  <ToggleButton key={t} value={t}>{t}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </CardContent>
          </Card>

          {/* Module grid */}
          <Typography variant="overline" sx={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1.2, color: 'text.disabled', mb: 1, px: 0.25 }}>
            DIGEST MODULES — toggle what gets included in your email
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2.5 }}>
            {DIGEST_MODULES.map(mod => {
              const on = prefs.modules[mod.key];
              return (
                <Card
                  key={mod.key}
                  elevation={0}
                  onClick={() => update(`modules.${mod.key}`, !on)}
                  sx={{
                    borderRadius: 2.5, cursor: 'pointer', transition: 'all 0.18s',
                    border: `1px solid ${on ? alpha(mod.color, 0.45) : alpha(theme.palette.divider, 0.1)}`,
                    bgcolor: on ? accentBg(mod.color) : 'transparent',
                    '&:hover': { border: `1px solid ${alpha(mod.color, 0.5)}`, bgcolor: accentBg(mod.color) },
                  }}
                >
                  <CardContent sx={{ p: 1.75, pb: '14px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ color: on ? mod.color : alpha(theme.palette.text.secondary, 0.5), transition: 'color 0.18s', display: 'flex' }}>
                          {mod.key === 'cvrMovers'       && <TrendingUp     sx={{ fontSize: 19 }} />}
                          {mod.key === 'hotColdStreaks'   && <Whatshot       sx={{ fontSize: 19 }} />}
                          {mod.key === 'playerWatchlist'  && <PersonAdd      sx={{ fontSize: 19 }} />}
                          {mod.key === 'teamPulse'        && <Groups         sx={{ fontSize: 19 }} />}
                          {mod.key === 'leagueInsights'   && <AutoAwesome    sx={{ fontSize: 19 }} />}
                          {mod.key === 'fantasyEdge'      && <MonetizationOn sx={{ fontSize: 19 }} />}
                        </Box>
                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.83rem', color: on ? 'text.primary' : 'text.secondary', transition: 'color 0.18s' }}>
                          {mod.label}
                        </Typography>
                      </Box>
                      <Switch
                        checked={on}
                        size="small"
                        onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); update(`modules.${mod.key}`, e.target.checked); }}
                        sx={{ ml: 0.5 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.74rem', lineHeight: 1.45, display: 'block' }}>
                      {mod.description}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {/* Player Watchlist */}
          {prefs.modules.playerWatchlist && (
            <Card elevation={0} sx={{ mb: 2.5, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <PersonAdd sx={{ fontSize: 17, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={700}>Player Watchlist</Typography>
                  <Chip label={`${prefs.watchedPlayers.length} / 20`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} />
                </Box>
                {prefs.watchedPlayers.length < 20 && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <TextField
                      size="small" placeholder="Player name"
                      value={playerName}
                      onChange={e => setPlayerName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPlayer(); } }}
                      sx={{ flex: '2 1 130px', minWidth: 130 }}
                    />
                    <TextField
                      size="small" placeholder="Team (NYY)"
                      value={playerTeam}
                      onChange={e => setPlayerTeam(e.target.value.toUpperCase().slice(0, 3))}
                      inputProps={{ maxLength: 3 }}
                      sx={{ flex: '0 1 92px', minWidth: 75 }}
                    />
                    <TextField
                      size="small" placeholder="CVR ≥"
                      value={playerThreshold}
                      onChange={e => setPlayerThreshold(e.target.value.replace(/\D/g, '').slice(0, 3))}
                      sx={{ flex: '0 1 74px', minWidth: 65 }}
                    />
                    <Button
                      variant="contained" size="small" startIcon={<Add />}
                      onClick={addPlayer} disabled={!playerName.trim()}
                      sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Add
                    </Button>
                  </Box>
                )}
                {prefs.watchedPlayers.length === 0 ? (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 1.5 }}>
                    No players added yet. Add up to 20 above.
                  </Typography>
                ) : (
                  <Stack spacing={0.6}>
                    {prefs.watchedPlayers.map((p, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.8, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1, fontSize: '0.84rem' }}>{p.name}</Typography>
                        {p.team && <Chip label={p.team} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.67rem', fontFamily: 'monospace', fontWeight: 700 }} />}
                        {p.cvrThreshold != null && <Chip label={`CVR ≥ ${p.cvrThreshold}`} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.67rem' }} />}
                        <IconButton size="small" onClick={() => removePlayer(i)} sx={{ p: 0.3, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          )}

          {/* Followed Teams */}
          {prefs.modules.teamPulse && (
            <Card elevation={0} sx={{ mb: 2.5, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Groups sx={{ fontSize: 17, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={700}>Followed Teams</Typography>
                  {prefs.followedTeams.length > 0 && (
                    <Chip label={prefs.followedTeams.length} size="small" color="primary" sx={{ height: 18, fontSize: '0.67rem', ml: 0.5 }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                  {MLB_TEAMS.map(t => {
                    const sel = prefs.followedTeams.includes(t);
                    return (
                      <Chip
                        key={t} label={t} size="small"
                        variant={sel ? 'filled' : 'outlined'}
                        color={sel ? 'primary' : 'default'}
                        onClick={() => toggleTeam(t)}
                        sx={{ fontWeight: 700, fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'monospace' }}
                      />
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Save / status bar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 2, minHeight: 36 }}>
        {saveError && <Alert severity="error" sx={{ py: 0, px: 1.5, fontSize: '0.78rem' }}>{saveError}</Alert>}
        {!saveError && savedAt && !dirty && (
          <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircle sx={{ fontSize: 14 }} /> Saved at {savedAt}
          </Typography>
        )}
        <Button
          variant={dirty ? 'contained' : 'outlined'}
          size="small"
          startIcon={saving ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : <Save />}
          disabled={saving || !dirty}
          onClick={save}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
        >
          Save Preferences
        </Button>
      </Box>
    </Box>
  );
}

const Settings = () => {
  const theme = useTheme();
  const { year } = useYear();
  const {
    displayDensity, defaultCategory, rowsPerPage, showGrades,
    animationsEnabled, autoRefreshScores, autoRefreshInterval,
    showAdvancedStats, colorCodeStats,
    setSetting, resetSettings, DEFAULT_SETTINGS
  } = useSettings();
  const { user, isPaid, startCheckout, openPortal } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [subStatus, setSubStatus] = useState(null); // { currentPeriodEnd, cancelAtPeriodEnd, isInviteCode }

  useEffect(() => {
    if (!user) return;
    fetch('/api/payments/status', {
      headers: { Authorization: `Bearer ${localStorage.getItem('tc-auth-token')}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSubStatus(d); })
      .catch(() => {});
  }, [user]);

  const [inviteCode, setInviteCode] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { success, message }

  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [themeMode, setThemeMode] = useState(() => {
    return JSON.parse(localStorage.getItem('theCycle-darkMode') || 'false');
  });

  useEffect(() => {
    checkHealth();
  }, []);

  const checkHealth = async () => {
    try {
      setLoadingHealth(true);
      const result = await healthCheck();
      setHealth({
        api: result.api || { status: 'ok' },
        redis: result.redis || { status: 'error' },
      });
    } catch {
      setHealth({ api: { status: 'error' }, redis: { status: 'error' } });
    } finally {
      setLoadingHealth(false);
    }
  };

  const StatusChip = ({ ok, label }) => (
    <Chip
      size="small"
      icon={ok ? <CheckCircle /> : <ErrorIcon />}
      label={label}
      color={ok ? 'success' : 'error'}
      variant="outlined"
    />
  );

  const SectionHeader = ({ icon, title, subtitle }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.1),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: theme.palette.primary.main,
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </Box>
    </Box>
  );

  const SettingRow = ({ icon, title, description, children, divider = true }) => (
    <>
      <ListItem sx={{ py: 1.5, px: 0 }}>
        <ListItemIcon sx={{ minWidth: 40 }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={<Typography variant="body2" fontWeight={600}>{title}</Typography>}
          secondary={<Typography variant="caption" color="text.secondary">{description}</Typography>}
        />
        {children}
      </ListItem>
      {divider && <Divider component="li" sx={{ opacity: 0.5 }} />}
    </>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Customize your analytics experience
        </Typography>
      </Box>

      {/* ── Tabs ── */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}` }}
        TabIndicatorProps={{ style: { height: 3, borderRadius: 2 } }}
      >
        <Tab
          label="General"
          icon={<SettingsIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', minHeight: 44, gap: 0.5 }}
        />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Email Digests
              {isPaid
                ? <Chip label="Pro" size="small" color="success" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700 }} />
                : <Lock sx={{ fontSize: 13, color: 'text.disabled' }} />}
            </Box>
          }
          icon={<Email sx={{ fontSize: 16 }} />}
          iconPosition="start"
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', minHeight: 44, gap: 0.5 }}
        />
      </Tabs>

      {/* ── Tab: General ── */}
      {activeTab === 0 && (<>

      {/* ── APPEARANCE ── */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader
            icon={<Palette fontSize="small" />}
            title="Appearance"
            subtitle="Theme and visual preferences"
          />
          <List disablePadding>
            <SettingRow
              icon={<DataObject color="primary" />}
              title="Dark Mode"
              description="Toggle dark/light theme"
            >
              <Switch
                checked={themeMode}
                onChange={(e) => {
                  setThemeMode(e.target.checked);
                  localStorage.setItem('theCycle-darkMode', JSON.stringify(e.target.checked));
                  window.dispatchEvent(new StorageEvent('storage', {
                    key: 'theCycle-darkMode',
                    newValue: JSON.stringify(e.target.checked),
                  }));
                }}
              />
            </SettingRow>

            <SettingRow
              icon={<ViewCompact color="primary" />}
              title="Display Density"
              description="Adjust spacing and padding throughout the app"
            >
              <ToggleButtonGroup
                value={displayDensity}
                exclusive
                onChange={(_, v) => v && setSetting('displayDensity', v)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1.5, py: 0.5, fontSize: '0.75rem', fontWeight: 600,
                    textTransform: 'none',
                  }
                }}
              >
                <ToggleButton value="compact">
                  <Tooltip title="Less padding, more data on screen"><span>Compact</span></Tooltip>
                </ToggleButton>
                <ToggleButton value="comfortable">
                  <Tooltip title="Balanced spacing"><span>Comfortable</span></Tooltip>
                </ToggleButton>
                <ToggleButton value="spacious">
                  <Tooltip title="More breathing room"><span>Spacious</span></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </SettingRow>

            <SettingRow
              icon={<Animation color="primary" />}
              title="Animations"
              description="Page transitions and micro-interactions"
              divider={false}
            >
              <Switch
                checked={animationsEnabled}
                onChange={(e) => setSetting('animationsEnabled', e.target.checked)}
              />
            </SettingRow>
          </List>
        </CardContent>
      </Card>

      {/* ── DATA DISPLAY ── */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader
            icon={<BarChart fontSize="small" />}
            title="Data & Stats"
            subtitle="Control how statistics are displayed"
          />
          <List disablePadding>
            <SettingRow
              icon={<SportsBaseball color="primary" />}
              title="Default Category"
              description="Starting stat category on Players and Leaders"
            >
              <ToggleButtonGroup
                value={defaultCategory}
                exclusive
                onChange={(_, v) => v && setSetting('defaultCategory', v)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1.5, py: 0.5, fontSize: '0.75rem', fontWeight: 600,
                    textTransform: 'none',
                  }
                }}
              >
                <ToggleButton value="batting">Batting</ToggleButton>
                <ToggleButton value="pitching">Pitching</ToggleButton>
              </ToggleButtonGroup>
            </SettingRow>

            <SettingRow
              icon={<TableRows color="primary" />}
              title="Rows Per Page"
              description="Default number of rows in data tables"
            >
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={rowsPerPage}
                  onChange={(e) => setSetting('rowsPerPage', e.target.value)}
                  sx={{ fontSize: '0.8rem', fontWeight: 600 }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </SettingRow>

            <SettingRow
              icon={<Thermostat color="primary" />}
              title="Heat-Map Colors"
              description="Color-code stat cells by percentile rank"
            >
              <Switch
                checked={colorCodeStats}
                onChange={(e) => setSetting('colorCodeStats', e.target.checked)}
              />
            </SettingRow>

            <SettingRow
              icon={<SportsCricket color="primary" />}
              title="Letter Grades"
              description="Show A+ through F grades on rate stats"
            >
              <Switch
                checked={showGrades}
                onChange={(e) => setSetting('showGrades', e.target.checked)}
              />
            </SettingRow>

            <SettingRow
              icon={<BarChart color="primary" />}
              title="Advanced Stats"
              description="Show WAR, wRC+, FIP, CVR columns by default"
              divider={false}
            >
              <Switch
                checked={showAdvancedStats}
                onChange={(e) => setSetting('showAdvancedStats', e.target.checked)}
              />
            </SettingRow>
          </List>
        </CardContent>
      </Card>

      {/* ── LIVE DATA ── */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader
            icon={<AutorenewRounded fontSize="small" />}
            title="Live Data"
            subtitle="Score refresh and real-time updates"
          />
          <List disablePadding>
            <SettingRow
              icon={<Refresh color="primary" />}
              title="Auto-Refresh Scores"
              description="Automatically refresh live game scores"
            >
              <Switch
                checked={autoRefreshScores}
                onChange={(e) => setSetting('autoRefreshScores', e.target.checked)}
              />
            </SettingRow>

            <SettingRow
              icon={<Speed color="primary" />}
              title="Refresh Interval"
              description={`Update every ${autoRefreshInterval} seconds`}
              divider={false}
            >
              <Box sx={{ width: 140, mr: 1 }}>
                <Slider
                  value={autoRefreshInterval}
                  onChange={(_, v) => setSetting('autoRefreshInterval', v)}
                  min={10}
                  max={120}
                  step={5}
                  disabled={!autoRefreshScores}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}s`}
                  size="small"
                  marks={[
                    { value: 10, label: '10s' },
                    { value: 120, label: '2m' },
                  ]}
                  sx={{
                    '& .MuiSlider-markLabel': { fontSize: '0.75rem' },
                  }}
                />
              </Box>
            </SettingRow>
          </List>
        </CardContent>
      </Card>

      {/* ── SEASON ── */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader
            icon={<CalendarMonth fontSize="small" />}
            title="Season"
          />
          <List disablePadding>
            <SettingRow
              icon={<CalendarMonth color="primary" />}
              title="Active Season"
              description={`Currently viewing ${year} season data`}
              divider={false}
            >
              <Chip label={year} color="primary" size="small" />
            </SettingRow>
          </List>
        </CardContent>
      </Card>

      {/* ── SYSTEM HEALTH ── */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <SectionHeader
              icon={<Wifi fontSize="small" />}
              title="System Health"
              subtitle="Backend connectivity"
            />
            <Chip
              icon={<Refresh />} label="Refresh" size="small" variant="outlined"
              onClick={checkHealth} sx={{ cursor: 'pointer' }}
            />
          </Stack>

          {loadingHealth ? (
            <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          ) : !health ? (
            <Alert severity="error">Unable to check system health</Alert>
          ) : (
            <List disablePadding>
              <ListItem sx={{ py: 1 }}>
                <ListItemIcon><Wifi color={health.api?.status === 'ok' ? 'success' : 'error'} /></ListItemIcon>
                <ListItemText primary="API Server" secondary="Data & analytics" />
                <StatusChip ok={health.api?.status === 'ok'} label={health.api?.status === 'ok' ? 'Connected' : 'Down'} />
              </ListItem>
              <Divider component="li" sx={{ opacity: 0.5 }} />
              <ListItem sx={{ py: 1 }}>
                <ListItemIcon><Storage color={health.redis?.status === 'connected' ? 'success' : 'error'} /></ListItemIcon>
                <ListItemText primary="Database" secondary="Stats & player data" />
                <StatusChip ok={health.redis?.status === 'connected'} label={health.redis?.status === 'connected' ? 'Connected' : 'Down'} />
              </ListItem>
            </List>
          )}
        </CardContent>
      </Card>

      {/* ── SUBSCRIPTION ── */}
      {user && (
        <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${alpha(isPaid ? theme.palette.success.main : theme.palette.primary.main, 0.25)}` }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <SectionHeader
              icon={<WorkspacePremium fontSize="small" />}
              title="Subscription"
              subtitle="Manage your Pro membership"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip
                    label={isPaid ? 'Pro' : 'Free'}
                    size="small"
                    color={isPaid ? 'success' : 'default'}
                    icon={isPaid ? <CheckCircle /> : undefined}
                    sx={{ fontWeight: 700 }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {isPaid ? 'The Cycle Pro' : 'The Cycle Free'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {isPaid
                    ? (() => {
                        if (subStatus?.isInviteCode) {
                          if (subStatus?.proDaysLeft != null) {
                            return `Pro access — ${subStatus.proDaysLeft} day${subStatus.proDaysLeft !== 1 ? 's' : ''} remaining · Expires ${new Date(subStatus.proExpiresAt).toLocaleDateString()}`;
                          }
                          return 'Active via invite code — Pro access granted.';
                        }
                        if (subStatus?.cancelAtPeriodEnd && subStatus?.currentPeriodEnd) {
                          const daysLeft = Math.max(0, Math.ceil((subStatus.currentPeriodEnd * 1000 - Date.now()) / 86400000));
                          return `Cancels in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} · Access until ${new Date(subStatus.currentPeriodEnd * 1000).toLocaleDateString()}`;
                        }
                        if (subStatus?.currentPeriodEnd) {
                          const daysLeft = Math.max(0, Math.ceil((subStatus.currentPeriodEnd * 1000 - Date.now()) / 86400000));
                          return `Renews in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} · Next bill ${new Date(subStatus.currentPeriodEnd * 1000).toLocaleDateString()}`;
                        }
                        return 'Full access to all Pro features. Billed monthly.';
                      })()
                    : 'Upgrade to Pro for advanced analytics, unlimited AI, and more.'}
                </Typography>
              </Box>
              {isPaid ? (
                subStatus?.isInviteCode ? (
                  <Chip
                    label="Invite Code Pro"
                    color="success"
                    variant="outlined"
                    size="small"
                    icon={<WorkspacePremium sx={{ fontSize: 14 }} />}
                    sx={{ fontWeight: 700 }}
                  />
                ) : (
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<OpenInNew fontSize="small" />}
                  disabled={portalLoading}
                  onClick={async () => {
                    setPortalLoading(true);
                    try { await openPortal(); } finally { setPortalLoading(false); }
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {portalLoading ? <CircularProgress size={16} /> : 'Manage Subscription'}
                </Button>
                )
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<WorkspacePremium />}
                  disabled={checkoutLoading}
                  onClick={async () => {
                    setCheckoutLoading(true);
                    try { await startCheckout(); } finally { setCheckoutLoading(false); }
                  }}
                  sx={{
                    textTransform: 'none', fontWeight: 700,
                    background: 'linear-gradient(135deg, #1a3a6e, #e8722a)',
                    '&:hover': { background: 'linear-gradient(135deg, #162f5a, #c96520)' },
                  }}
                >
                  {checkoutLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Upgrade — $8/mo'}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── INVITE CODE ── */}
      {user && !isPaid && (
        <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: theme.palette.warning.main,
              }}>
                <CardGiftcard fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>Redeem Invite Code</Typography>
                <Typography variant="caption" color="text.secondary">Have a code? Enter it below for free Pro access.</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                size="small"
                placeholder="XXXX-XXXX-XXXX"
                value={inviteCode}
                onChange={e => { setInviteCode(e.target.value.toUpperCase()); setInviteResult(null); }}
                inputProps={{ maxLength: 14, style: { fontFamily: 'monospace', letterSpacing: 2 } }}
                sx={{ flex: 1 }}
                disabled={inviteLoading}
              />
              <Button
                variant="contained"
                size="small"
                disabled={inviteLoading || inviteCode.length < 14}
                onClick={async () => {
                  setInviteLoading(true);
                  setInviteResult(null);
                  try {
                    const token = localStorage.getItem('tc-auth-token');
                    const res = await fetch('/api/payments/redeem', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ code: inviteCode }),
                    });
                    const data = await res.json();
                    if (res.ok) {
                      setInviteResult({ success: true, message: data.message || 'Welcome to Pro!' });
                      // Refresh user so tier updates immediately
                      setTimeout(() => window.location.reload(), 1500);
                    } else {
                      setInviteResult({ success: false, message: data.error || 'Failed to redeem code' });
                    }
                  } catch (_) {
                    setInviteResult({ success: false, message: 'Network error — please try again' });
                  } finally {
                    setInviteLoading(false);
                  }
                }}
                sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {inviteLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Redeem'}
              </Button>
            </Box>
            {inviteResult && (
              <Alert severity={inviteResult.success ? 'success' : 'error'} sx={{ mt: 1.5, py: 0.5 }}>
                {inviteResult.message}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── RESET ── */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" fontWeight={600}>Reset All Settings</Typography>
              <Typography variant="caption" color="text.secondary">
                Restore all preferences to their default values
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<RestartAlt />}
              onClick={resetSettings}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Reset
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── ABOUT ── */}
      <Card elevation={0} sx={{ borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: '1rem' }}>About The Cycle</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The Cycle is a comprehensive MLB analytics platform testing every possible baseball statistic.
            Features 40+ statistical categories, pitch-level splits, macro analytics, and real-time game tracking.
          </Typography>
        </CardContent>
      </Card>
      </>)}

      {/* ── Tab: Email Digests ── */}
      {activeTab === 1 && (
        <EmailDigestsTab
          isPaid={isPaid}
          startCheckout={startCheckout}
          user={user}
        />
      )}
    </Box>
  );
};

export default Settings;
